import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  newBusinessProblems, type NewBusinessAccess, type NewBusinessFormDto, type NewBusinessInput, type NewBusinessPreviewDto, type NewBusinessResultDto,
  type OwnerSetupIssuedDto, type PlatformOrgDetail, type SetupDelivery
} from "@delta/shared";
import { useLang, type T } from "../../i18n";
import { api, ApiFailure } from "../../lib/api";
import { Icon } from "../../components/Icon";
import { Field, Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { errText, fieldErrs, FIELD } from "./common";

// Add business (30 Oct 2026, 21:47; migration 053), on the Dashboard and the Businesses list. Three steps in one
// dialog: the form, a preview of exactly what will be created (business, owner, package dates and limits, or
// pending without access), and the result with the owner's setup code. The server checks everything again; the form
// keeps every field after an error. The requestId is made once per dialog, so a double click or a retry after a lost
// answer cannot create a second business. The Super Admin never chooses or sees the owner's password: the owner sets it
// with the setup code, which is sent by a configured provider or, labelled "not sent", shown here once.

type Step = "form" | "preview" | "done";

const blank = (today: string): NewBusinessInput => ({
  requestId: crypto.randomUUID(), businessName: "", language: "ar", ownerName: "", mobile: "", email: "", access: "trial", planId: "", startsOn: today, endsOn: "",
  reason: "", delivery: "manual"
});

const localErrs = (t: T, f: NewBusinessInput) => Object.fromEntries(Object.entries(newBusinessProblems(f)).map(([k, c]) => [k, t(FIELD[c!] ?? "fieldRequired")]));

export function AddBusinessButton() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="btn btn-primary" data-testid="pf-add-business" onClick={() => setOpen(true)}><Icon name="plus" />{t("abAdd")}</button>
      {open && <AddBusinessDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function AddBusinessDialog({ onClose }: { onClose(): void }) {
  const { t, L, lang } = useLang();
  const nav = useNavigate();
  const [form, setForm] = useState<NewBusinessFormDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [f, setF] = useState<NewBusinessInput | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<Step>("form");
  const [preview, setPreview] = useState<NewBusinessPreviewDto | null>(null);
  const [sameOk, setSameOk] = useState(false);
  const [result, setResult] = useState<NewBusinessResultDto | null>(null);
  useEffect(() => {
    api<NewBusinessFormDto>("GET", "/platform/new-business").then((d) => { setForm(d); setF(blank(d.today)); }, (e) => setFailed(errText(t, e)));
  }, [t]);
  const set = (p: Partial<NewBusinessInput>) => { setAlert(null); setF((x) => ({ ...x!, ...p })); setErrs((e) => { const n = { ...e }; Object.keys(p).forEach((k) => delete n[k]); return n; }); };
  const body = (x: NewBusinessInput): NewBusinessInput => ({
    ...x, email: x.email?.trim() || undefined, planId: x.access === "pending" ? undefined : x.planId || undefined,
    startsOn: x.access === "paid" ? x.startsOn : undefined, endsOn: x.access === "paid" ? x.endsOn : undefined, confirmSameName: sameOk
  });
  const fail = (e: unknown) => {
    setErrs(fieldErrs(t, e));
    setAlert(e instanceof ApiFailure && e.code === "validation" ? t("fixRequired") : errText(t, e));
  };

  const toPreview = async () => {
    const local = localErrs(t, f!);
    setErrs(local); setAlert(null);
    if (Object.keys(local).length) { setAlert(t("fixRequired")); return; }
    setBusy(true);
    try { setPreview(await api<NewBusinessPreviewDto>("POST", "/platform/organizations/preview", body(f!))); setSameOk(false); setStep("preview"); }
    catch (e) { fail(e); } finally { setBusy(false); }
  };
  const create = async () => {
    setBusy(true); setAlert(null);
    try { setResult(await api<NewBusinessResultDto>("POST", "/platform/organizations", body(f!))); setStep("done"); }
    catch (e) {
      // Back to the form with every field kept, the problem shown at its field.
      fail(e);
      if (!(e instanceof ApiFailure && e.code === "same_name")) setStep("form");
    } finally { setBusy(false); }
  };

  if (failed || !form || !f) {
    return (
      <Modal title={t("abTitle")} onClose={onClose} onSubmit={onClose} saveLabel={t("close")} testId="pf-add-dialog">
        <div className="pf-dialog">{failed ? <div className="state-box error"><Icon name="alert" /><h3>{failed}</h3></div> : <p>{t("pfLoading")}</p>}</div>
      </Modal>
    );
  }
  const plans = form.plans.filter((p) => (f.access === "trial" ? p.kind === "trial" : p.kind === "paid"));
  const provider = (d: SetupDelivery) => d === "manual" || form.providers[d] === "configured";

  if (step === "done" && result) {
    return (
      <Modal title={t("abDoneTitle")} sub={result.replayed ? t("abReplayed") : undefined} wide onClose={onClose} testId="pf-add-done" saveLabel={t("abOpenBusiness")} cancelLabel={t("close")}
        onSubmit={() => { onClose(); nav(`/platform/businesses/${result.organizationId}`); }}>
        <div className="pf-dialog">
          <div className="dlg-alert show ok" role="status"><Icon name="check" /><span>{t(preview?.status === "pending" ? "abDonePending" : "abDoneActive", { x: f.businessName.trim() })}</span></div>
          {result.setup ? <SetupIssued s={result.setup} /> : <p className="pf-small" data-testid="pf-add-no-code">{t("abReplayedNoCode")}</p>}
        </div>
      </Modal>
    );
  }

  if (step === "preview" && preview) {
    const p = preview.package;
    return (
      <Modal title={t("abPreviewTitle")} sub={t("abPreviewSub")} wide onClose={onClose} busy={busy} alert={alert} testId="pf-add-preview" saveTestId="pf-add-create" saveLabel={t("abCreate")}
        saveDisabled={preview.sameName.length > 0 && !sameOk} onSubmit={() => void create()}
        extra={<button type="button" className="btn" data-testid="pf-add-back" onClick={() => { setStep("form"); setAlert(null); }}><Icon name="back" />{t("abBack")}</button>}>
        <div className="pf-dialog">
          <dl className="pf-kv" data-testid="pf-add-summary">
            <dt>{t("abBusiness")}</dt><dd><b>{preview.business.name}</b> · {preview.business.language === "ar" ? "العربية" : "English"}</dd>
            <dt>{t("pfOwner")}</dt><dd>{preview.owner.name} · <span className="num" dir="ltr">{preview.owner.mobile}</span>{preview.owner.email && <> · <span dir="ltr">{preview.owner.email}</span></>}</dd>
            <dt>{t("pfColStatus")}</dt><dd><span className={`pill ${preview.status === "active" ? "good" : "warn"}`} data-testid="pf-add-status">{t(`pfStatus_${preview.status}`)}</span></dd>
            <dt>{t("abAccess")}</dt><dd data-testid="pf-add-package">{p ? <>
              <b>{L(p)}</b> · {t(p.kind === "trial" ? "abKindTrial" : "abKindPaid")} · v{p.version}
              <div className="num">{p.startsOn} → {p.endsOn}{p.trialDays ? ` · ${t("pkgTrialDays", { n: p.trialDays })}` : ""}</div>
              <div className="pf-small num">{t("obLimits", { u: p.limits.users, b: p.limits.branches, w: p.limits.warehouses, t: p.limits.treasuries, p: p.limits.posDevices })}</div>
              <div className="pf-small">{p.features.map((x) => t(`pkgFeature_${x}`)).join(" · ")}</div>
            </> : <span className="bad-text">{t("abPendingNoAccess")}</span>}</dd>
            <dt>{t("abDelivery")}</dt><dd>{t(`abDelivery_${preview.delivery}`)} · <span className="pf-small">{t("abSetupValid", { n: preview.setupHours })}</span></dd>
            <dt>{t("abReason")}</dt><dd>{f.reason.trim()}</dd>
          </dl>
          <p className="pf-small">{t("abNothingElse")}</p>
          {preview.sameName.length > 0 && <div className="dlg-alert show" role="alert" data-testid="pf-add-same-name"><Icon name="alert" /><span>
            {t("abSameName", { x: preview.sameName.map((o) => `${lang === "ar" ? o.nameAr : o.nameEn} (${t(`pfStatus_${o.status}`)})`).join("، ") })}
            <label className="check"><input type="checkbox" data-testid="pf-add-same-ok" checked={sameOk} onChange={(e) => setSameOk(e.target.checked)} /> {t("abSameNameOk")}</label></span></div>}
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={t("abTitle")} sub={t("abSub")} wide onClose={onClose} busy={busy} alert={alert} testId="pf-add-dialog" saveTestId="pf-add-preview-btn" saveLabel={t("abPreview")} onSubmit={() => void toPreview()}>
      <div className="pf-dialog ab-form">
        <h4>{t("abBusiness")}</h4>
        <div className="pf-dates">
          <Field label={t("signupBusinessName")} req htmlFor="ab-name" error={errs.businessName}>
            <input id="ab-name" className="control" value={f.businessName} onChange={(e) => set({ businessName: e.target.value })} /></Field>
          <Field label={t("pfLanguage")} req error={errs.language}>
            <div className="segmented" role="radiogroup" aria-label={t("pfLanguage")}>{(["ar", "en"] as const).map((l) => (
              <label key={l}><input type="radio" name="ab-lang" checked={f.language === l} onChange={() => set({ language: l })} /><span>{l === "ar" ? "العربية" : "English"}</span></label>))}</div></Field>
        </div>
        <h4>{t("pfOwner")}</h4>
        <div className="pf-dates">
          <Field label={t("signupOwnerName")} req htmlFor="ab-owner" error={errs.ownerName}>
            <input id="ab-owner" className="control" value={f.ownerName} onChange={(e) => set({ ownerName: e.target.value })} /></Field>
          <Field label={t("signupMobile")} req htmlFor="ab-mobile" error={errs.mobile}>
            <input id="ab-mobile" className="control num" dir="ltr" type="tel" style={{ textAlign: "start" }} placeholder={t("mobilePh")} value={f.mobile} onChange={(e) => set({ mobile: e.target.value })} /></Field>
          <Field label={t("abEmailOptional")} htmlFor="ab-email" error={errs.email}>
            <input id="ab-email" className="control" dir="ltr" type="email" placeholder={t("signupEmailPh")} value={f.email} onChange={(e) => set({ email: e.target.value })} /></Field>
        </div>
        <span className="pf-small">{t("abNoPassword")}</span>
        <h4>{t("abAccess")}</h4>
        <div className="pf-modes" role="radiogroup" aria-label={t("abAccess")} data-testid="pf-add-access">
          {(["trial", "paid", "pending"] as NewBusinessAccess[]).map((a) => (
            <label key={a} className={`pf-mode ${f.access === a ? "on" : ""}`}>
              <input type="radio" name="ab-access" value={a} checked={f.access === a} onChange={() => set({ access: a, planId: "" })} />
              <span><b>{t(`abAccess_${a}`)}</b><span className="pf-small">{t(`abAccessHint_${a}`)}</span></span>
            </label>
          ))}
        </div>
        {f.access !== "pending" && (
          <div className="pf-dates">
            <Field label={t("abPackage")} req htmlFor="ab-plan" error={errs.planId}>
              <select id="ab-plan" className="control" value={f.planId} onChange={(e) => set({ planId: e.target.value })}>
                <option value="">{t("abChoose")}</option>
                {plans.map((p) => <option key={p.id} value={p.id}>{L(p)}{p.kind === "trial" ? ` · ${t("pkgTrialDays", { n: p.trialDays ?? 0 })}` : ""}</option>)}
              </select>
              {!plans.length && <span className="pf-small bad-text">{t("abNoPlans")}</span>}</Field>
            {f.access === "paid" && <>
              <Field label={t("pfStartsOn")} req htmlFor="ab-start" error={errs.startsOn}><input id="ab-start" className="control num" type="date" min={form.today} value={f.startsOn} onChange={(e) => set({ startsOn: e.target.value })} /></Field>
              <Field label={t("pfEndsOn")} req htmlFor="ab-end" error={errs.endsOn}><input id="ab-end" className="control num" type="date" min={f.startsOn || form.today} value={f.endsOn} onChange={(e) => set({ endsOn: e.target.value })} /></Field>
            </>}
          </div>
        )}
        <h4>{t("abDelivery")}</h4>
        <div className="pf-modes" role="radiogroup" aria-label={t("abDelivery")} data-testid="pf-add-delivery">
          {(["sms", "email", "manual"] as SetupDelivery[]).map((d) => {
            const off = !provider(d);
            return (
              <label key={d} className={`pf-mode ${f.delivery === d ? "on" : ""} ${off ? "off" : ""}`}>
                <input type="radio" name="ab-delivery" value={d} checked={f.delivery === d} disabled={off} onChange={() => set({ delivery: d })} />
                <span><b>{t(`abDelivery_${d}`)}</b><span className="pf-small">{t(`abDeliveryHint_${d}`, { n: 48 })}</span>
                  {off && <span className="pill bad">{t("abNoProvider")}</span>}</span>
              </label>
            );
          })}
        </div>
        {errs.delivery && <div className="ferr" role="alert">{errs.delivery}</div>}
        <Field label={t("abReason")} req htmlFor="ab-reason" error={errs.reason} hint={t("abReasonHint")}>
          <textarea id="ab-reason" className="control" rows={2} value={f.reason} onChange={(e) => set({ reason: e.target.value })} /></Field>
      </div>
    </Modal>
  );
}

/** The setup code just issued: sent (masked destination), or shown once to pass on by hand, labelled as not sent. */
export function SetupIssued({ s }: { s: OwnerSetupIssuedDto }) {
  const { t, lang } = useLang();
  const toast = useToast();
  const until = new Date(s.expiresAt).toLocaleString(lang === "ar" ? "ar-SA-u-nu-latn" : "en-GB", { dateStyle: "medium", timeStyle: "short" });
  if (s.delivery !== "manual") {
    return <div className="dlg-alert show ok" role="status" data-testid="pf-setup-sent"><Icon name="check" /><span>{t("abSentVia", { c: t(`abDelivery_${s.delivery}`), to: s.destination ?? "", d: until })}</span></div>;
  }
  const link = `${window.location.origin}${s.link}`;
  const copy = (v: string) => { void navigator.clipboard?.writeText(v).then(() => toast(t("abCopied")), () => undefined); };
  return (
    <div className="ab-manual" data-testid="pf-setup-manual">
      <span className="pill warn">{t("abNotSent")}</span>
      <p className="pf-small">{t("abManualHint", { d: until })}</p>
      <label className="pf-small" htmlFor="ab-code">{t("abSetupCode")}</label>
      <code id="ab-code" className="rs-code num" dir="ltr" data-testid="pf-setup-code">{s.code}</code>
      <label className="pf-small" htmlFor="ab-link">{t("abSetupLink")}</label>
      <input id="ab-link" className="control" dir="ltr" readOnly value={link} data-testid="pf-setup-link" onFocus={(e) => e.target.select()} />
      <div className="pf-row">
        <button type="button" className="btn btn-sm" onClick={() => copy(s.code!)}><Icon name="copy" />{t("abCopyCode")}</button>
        <button type="button" className="btn btn-sm" onClick={() => copy(link)}><Icon name="copy" />{t("abCopyLink")}</button>
      </div>
    </div>
  );
}

/** A new setup code for the owner of a business added here, while the owner has not set a password yet. */
export function OwnerSetupModal({ o, onClose, onDone }: { o: PlatformOrgDetail; onClose(): void; onDone(): void }) {
  const { t } = useLang();
  const [delivery, setDelivery] = useState<SetupDelivery>("manual");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState<string | null>(null);
  const [issued, setIssued] = useState<OwnerSetupIssuedDto | null>(null);
  const [providers, setProviders] = useState<NewBusinessFormDto["providers"] | null>(null);
  const once = useRef(false);
  useEffect(() => { if (!once.current) { once.current = true; api<NewBusinessFormDto>("GET", "/platform/new-business").then((d) => setProviders(d.providers), () => undefined); } }, []);
  if (issued) {
    return <Modal title={t("abNewCode")} onClose={() => { onDone(); onClose(); }} onSubmit={() => { onDone(); onClose(); }} saveLabel={t("close")} cancelLabel={t("close")} testId="pf-setup-issued">
      <div className="pf-dialog"><SetupIssued s={issued} /></div></Modal>;
  }
  const off = (d: SetupDelivery) => d !== "manual" && (providers?.[d] !== "configured" || (d === "email" && !o.signupDetail?.email));
  return (
    <Modal title={t("abNewCode")} sub={t("abNewCodeSub")} onClose={onClose} busy={busy} alert={alert} testId="pf-setup-dialog" saveLabel={t("abIssue")} saveDisabled={reason.trim().length < 5}
      onSubmit={async () => {
        setBusy(true); setAlert(null);
        try { setIssued(await api<OwnerSetupIssuedDto>("POST", `/platform/organizations/${o.id}/owner-setup`, { delivery, reason })); }
        catch (e) { setAlert(errText(t, e)); } finally { setBusy(false); }
      }}>
      <div className="pf-dialog">
        <div className="pf-modes" role="radiogroup" aria-label={t("abDelivery")}>
          {(["sms", "email", "manual"] as SetupDelivery[]).map((d) => (
            <label key={d} className={`pf-mode ${delivery === d ? "on" : ""} ${off(d) ? "off" : ""}`}>
              <input type="radio" name="ab-redelivery" checked={delivery === d} disabled={off(d)} onChange={() => setDelivery(d)} />
              <span><b>{t(`abDelivery_${d}`)}</b>{off(d) && <span className="pill bad">{t("abNoProvider")}</span>}</span>
            </label>
          ))}
        </div>
        <Field label={t("pfReason")} req htmlFor="ab-re-reason"><textarea id="ab-re-reason" className="control" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      </div>
    </Modal>
  );
}
